/**
 * @fileoverview Collections Management Hook
 *
 * Provides CRUD operations for user collections (folders for organizing saved items).
 * Collections can be pinned, reordered, and contain multiple saved items.
 *
 * @module hooks/useCollections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useDevMode } from '@/contexts/DevModeContext';
import { logEventDirect } from './useEventLogger';
import { transformSavedItem } from './useSavedItems';

/**
 * Represents a user collection.
 */
export interface Collection {
  /** Unique collection ID */
  id: string;
  /** Owner's user ID */
  user_id: string;
  /** Collection display name */
  name: string;
  /** Optional description */
  description: string | null;
  /** Emoji icon for visual identification */
  icon: string;
  /** Tailwind color class for theming */
  color: string;
  /** Whether this is the default "All Saves" collection */
  is_default: boolean;
  /** Whether other users can add items */
  is_collaborative: boolean;
  /** Optional cover image URL */
  cover_image_url: string | null;
  /** ISO timestamp of creation */
  created_at: string;
  /** ISO timestamp of last update */
  updated_at: string;
  /** ISO timestamp if pinned (null = not pinned) */
  pinned_at: string | null;
  /** Number of items in collection (computed) */
  item_count?: number;
  /** First 4 item image URLs for preview mosaic (computed) */
  preview_images?: string[];
  /** ISO timestamp of most recently added item (computed) */
  latest_item_added_at?: string | null;
  /** Number of followers for collaborative collections (computed) */
  follower_count?: number;
  /** Whether the collection is publicly visible */
  is_public?: boolean;
}

/**
 * Input for creating a new collection.
 */
export interface CreateCollectionInput {
  /** Collection name (required) */
  name: string;
  /** Optional description */
  description?: string;
  /** Emoji icon (defaults to 📁) */
  icon?: string;
  /** Color class (defaults to gray) */
  color?: string;
  /** Allow others to add items */
  is_collaborative?: boolean;
  /** Make collection publicly visible */
  is_public?: boolean;
}

/**
 * Hook for managing user collections.
 *
 * Features:
 * - CRUD operations for collections
 * - Add/remove items from collections
 * - Pin collections to top of list
 * - Reorder items within a collection
 * - Auto-calculates item counts
 *
 * Sorting: Pinned collections first (by pinned_at DESC), then by created_at DESC.
 *
 * @returns {Object} Collection management interface
 * @property {Collection[]} collections - All user collections with item counts
 * @property {boolean} isLoading - True while fetching
 * @property {Error | null} error - Fetch error if any
 * @property {UseMutationResult} createCollection - Create new collection
 * @property {UseMutationResult} updateCollection - Update collection properties
 * @property {UseMutationResult} deleteCollection - Delete a collection
 * @property {UseMutationResult} addItemToCollection - Add saved item to collection
 * @property {UseMutationResult} removeItemFromCollection - Remove item from collection
 * @property {Function} getCollectionItems - Fetch items in a collection
 * @property {UseMutationResult} reorderCollectionItems - Update item positions
 * @property {UseMutationResult} pinCollection - Pin collection to top
 * @property {UseMutationResult} unpinCollection - Unpin collection
 *
 * @example
 * const { collections, createCollection, pinCollection } = useCollections();
 *
 * // Create a collection
 * createCollection.mutate({ name: 'Summer Outfits', icon: '☀️' });
 *
 * // Pin a collection
 * pinCollection.mutate(collectionId);
 *
 * // Get items in a collection
 * const items = await getCollectionItems(collectionId);
 */
export function useCollections() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isDevMode } = useDevMode();

  const { data: collections = [], isLoading, error } = useQuery({
    queryKey: ['collections', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Use the optimized database view that computes stats in SQL
      // This eliminates N+1 queries and moves computation to the database
      const { data: collectionsData, error: collectionsError } = await supabase
        .from('collections_with_stats' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('pinned_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (collectionsError) throw collectionsError;

      // Transform to match Collection interface
      return (collectionsData || []).map((collection: any) => ({
        ...collection,
        // Convert postgres array to JS array for preview_images
        preview_images: collection.preview_images || [],
      })) as Collection[];
    },
    enabled: !!user,
    // Keep previous data during refetches to prevent flicker
    placeholderData: (previousData) => previousData ?? [],
    // Optimize remounting: use cached data while refreshing in background
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: false, // Don't refetch if data exists
    structuralSharing: true,
  });

  const createCollection = useMutation({
    mutationFn: async (input: CreateCollectionInput) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('collections')
        .insert({
          ...input,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Collection;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
      
      // Log event for analytics
      logEventDirect('collection_created', 'creation', {
        is_collaborative: data.is_collaborative,
        has_description: !!data.description,
      }, user?.id);
    },
  });

  const updateCollection = useMutation({
    mutationFn: async ({ id, ...input }: Partial<Collection> & { id: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('collections')
        .update(input)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Collection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
    },
  });

  const deleteCollection = useMutation({
    mutationFn: async (collectionId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('collections')
        .delete()
        .eq('id', collectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
    },
  });

  const addItemToCollection = useMutation({
    mutationFn: async ({ collectionId, savedItemId }: { collectionId: string; savedItemId: string }) => {
      if (!user) throw new Error('Not authenticated');

      // Get current max position
      const { data: existingItems } = await supabase
        .from('collection_items')
        .select('position')
        .eq('collection_id', collectionId)
        .order('position', { ascending: false })
        .limit(1);

      const nextPosition = existingItems && existingItems.length > 0 
        ? existingItems[0].position + 1 
        : 0;

      const { data, error } = await supabase
        .from('collection_items')
        .insert({
          collection_id: collectionId,
          saved_item_id: savedItemId,
          position: nextPosition,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-items'] });
      queryClient.invalidateQueries({ queryKey: ['items-in-collections'] });
    },
  });

  const removeItemFromCollection = useMutation({
    mutationFn: async ({ collectionId, savedItemId }: { collectionId: string; savedItemId: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('collection_items')
        .delete()
        .eq('collection_id', collectionId)
        .eq('saved_item_id', savedItemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-items'] });
      queryClient.invalidateQueries({ queryKey: ['items-in-collections'] });
    },
  });

  const getCollectionItems = async (collectionId: string) => {
    const { data, error } = await supabase
      .from('collection_items')
      .select(`
        id,
        added_at,
        position,
        saved_items (
          *,
          flux_pages:page_id (
            id, url, tld, is_expired, process_status, processed_at, data, category,
            page_ai_data (
              ai_summary, ai_category, ai_tags, ai_person, ai_focal_point,
              platform, creator_name, creator_bio_link
            )
          )
        )
      `)
      .eq('collection_id', collectionId)
      .order('added_at', { ascending: false });

    if (error) throw error;

    // Transform each collection item's saved_items through the standard transformer
    return (data || []).map((item: any) => ({
      ...item,
      saved_items: item.saved_items ? transformSavedItem(item.saved_items) : null,
    }));
  };

  const reorderCollectionItems = useMutation({
    mutationFn: async ({ collectionId, items }: { collectionId: string; items: { id: string; position: number }[] }) => {
      if (!user) throw new Error('Not authenticated');

      // Update positions for all items
      const updates = items.map(({ id, position }) =>
        supabase
          .from('collection_items')
          .update({ position })
          .eq('id', id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) throw errors[0].error;
    },
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: ['collection-items', collectionId] });
    },
  });

  const pinCollection = useMutation({
    mutationFn: async (collectionId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('collections')
        .update({ pinned_at: new Date().toISOString() })
        .eq('id', collectionId)
        .select()
        .single();

      if (error) throw error;
      return data as Collection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
    },
  });

  const unpinCollection = useMutation({
    mutationFn: async (collectionId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('collections')
        .update({ pinned_at: null })
        .eq('id', collectionId)
        .select()
        .single();

      if (error) throw error;
      return data as Collection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections', user?.id] });
    },
  });

  return {
    collections: isDevMode ? [] : collections,
    isLoading,
    error,
    createCollection,
    updateCollection,
    deleteCollection,
    addItemToCollection,
    removeItemFromCollection,
    getCollectionItems,
    reorderCollectionItems,
    pinCollection,
    unpinCollection,
  };
}
